import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Mail, Bell, Megaphone, Loader2, Save, Check, AlertCircle, Info } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { createLogger } from '../../utils/logger'

const logger = createLogger('CommunicationPreferences')

const CommunicationPreferences = () => {
  const user = useAuthStore((state) => state.user)
  const updateUser = useAuthStore((state) => state.updateUser)

  const [preferences, setPreferences] = useState({
    marketing: false,
    research: false,
    newsletter: false
  })
  const [originalPreferences, setOriginalPreferences] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [lastUpdated, setLastUpdated] = useState(null)
  const [hasChanges, setHasChanges] = useState(false)

  // Load preferences on mount
  useEffect(() => {
    loadPreferences()
  }, [])

  // Check for changes
  useEffect(() => {
    const changed =
      preferences.marketing !== originalPreferences.marketing ||
      preferences.research !== originalPreferences.research ||
      preferences.newsletter !== originalPreferences.newsletter
    setHasChanges(changed)
  }, [preferences, originalPreferences])

  const loadPreferences = async () => {
    setIsLoading(true)
    try {
      const response = await fetch('/api/compliance/preferences')
      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Failed to load preferences')
      }

      if (data.success && data.data) {
        const prefs = {
          marketing: !data.data.marketingOptOut,
          research: !data.data.researchOptOut,
          newsletter: !data.data.newsletterOptOut
        }
        setPreferences(prefs)
        setOriginalPreferences(prefs)
        setLastUpdated(data.data.preferencesUpdatedAt)
      }
    } catch (error) {
      logger.error('Error loading preferences:', error)
      toast.error('Failed to load communication preferences')
    } finally {
      setIsLoading(false)
    }
  }

  const handleToggle = (field) => {
    setPreferences((prev) => ({
      ...prev,
      [field]: !prev[field]
    }))
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const response = await fetch('/api/compliance/preferences', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          marketingOptOut: !preferences.marketing,
          researchOptOut: !preferences.research,
          newsletterOptOut: !preferences.newsletter
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update preferences')
      }

      if (data.success && data.data) {
        // Update local state
        const prefs = {
          marketing: !data.data.marketingOptOut,
          research: !data.data.researchOptOut,
          newsletter: !data.data.newsletterOptOut
        }
        setPreferences(prefs)
        setOriginalPreferences(prefs)
        setLastUpdated(data.data.preferencesUpdatedAt)
        setHasChanges(false)

        // Update user in auth store
        if (data.data.user) {
          updateUser(data.data.user)
        }

        toast.success('Communication preferences updated!')
      }
    } catch (error) {
      logger.error('Error saving preferences:', error)
      toast.error(error.message || 'Failed to save preferences')
    } finally {
      setIsSaving(false)
    }
  }

  const handleReset = () => {
    setPreferences(originalPreferences)
    setHasChanges(false)
  }

  if (isLoading) {
    return (
      <div className="card p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-primary-600 animate-spin" />
          <span className="ml-3 text-gray-600 dark:text-gray-400">Loading preferences...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="card">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
            <Mail className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Communication Preferences
          </h2>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Choose which emails you'd like to receive from us
        </p>
        {lastUpdated && (
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
            Last updated: {new Date(lastUpdated).toLocaleString()}
          </p>
        )}
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Marketing Emails Toggle */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Megaphone className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Marketing Emails
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Receive promotional offers, discounts, and special announcements
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={preferences.marketing}
              onChange={() => handleToggle('marketing')}
              disabled={isSaving}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
          </label>
        </div>

        {/* Research Participation Toggle */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Bell className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Research Participation
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Invitations to participate in user research, surveys, and feedback sessions
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={preferences.research}
              onChange={() => handleToggle('research')}
              disabled={isSaving}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
          </label>
        </div>

        {/* Newsletter Toggle */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Mail className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Product Newsletter
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Monthly updates about new features, tips, and educational content
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={preferences.newsletter}
              onChange={() => handleToggle('newsletter')}
              disabled={isSaving}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
          </label>
        </div>

        {/* Info Notice */}
        <div className="flex items-start gap-2 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <Info className="w-4 h-4 text-gray-600 dark:text-gray-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-gray-600 dark:text-gray-400">
            <strong>Note:</strong> You'll always receive important transactional emails about your
            account, security, and service updates. These cannot be disabled.
          </p>
        </div>

        {/* Action Buttons */}
        <AnimatePresence>
          {hasChanges && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700"
            >
              <button
                onClick={handleReset}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-lg shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Save Changes</span>
                  </>
                )}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default CommunicationPreferences
